<template>
  <main class="grow topic-page">
    <section class="topic-hero">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <nav class="topic-breadcrumb" aria-label="Breadcrumb">
          <NuxtLink :to="localePath('/learn')">Learning center</NuxtLink>
          <span aria-hidden="true">/</span>
          <span>{{ page.title }}</span>
        </nav>
        <h1>{{ page.title }}.</h1>
        <p class="topic-promise">{{ page.promise }}</p>
        <p class="topic-answer">{{ page.answer }}</p>
      </div>
    </section>

    <section v-for="section in page.sections" :key="section.title" class="topic-section">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="topic-split">
          <h2>{{ section.title }}</h2>
          <div class="topic-table">
            <NuxtLink
              v-for="article in section.articles"
              :key="article.path"
              :to="localePath(article.path)"
              class="topic-row"
            >
              <span class="topic-row-main">
                <strong>{{ article.title }}</strong>
                <span>{{ article.description }}</span>
              </span>
              <i aria-hidden="true">→</i>
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <section class="topic-section">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="topic-split">
          <h2>{{ page.guideTitle }}</h2>
          <div>
            <ol class="topic-steps">
              <li v-for="step in page.guide" :key="step.title">
                <strong>{{ step.title }}</strong>
                <span>{{ step.copy }}</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </section>

    <section class="topic-section">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="topic-split">
          <h2>Key terms</h2>
          <dl class="topic-terms">
            <div v-for="item in page.terms" :key="item.term">
              <dt>{{ item.term }}</dt>
              <dd>{{ item.definition }}</dd>
            </div>
          </dl>
        </div>
      </div>
    </section>

    <section class="topic-section">
      <div class="mx-auto max-w-6xl px-4 sm:px-6">
        <div class="topic-split">
          <h2>Common questions</h2>
          <div class="topic-faq">
            <details v-for="item in page.faq" :key="item.q">
              <summary>{{ item.q }}</summary>
              <p>{{ item.a }}</p>
            </details>
          </div>
        </div>
      </div>
    </section>

    <RelatedPages :path="`/learn/topics/${page.slug}`" :title="page.title" />

    <Cta03 />
  </main>
</template>

<script setup>
import { learnTopicsBySlug } from '~/data/learn-topics';
import { localizedUrl } from '~/data/locales';

const route = useRoute();
const page = computed(() => learnTopicsBySlug[route.params.slug]);

if (!page.value) {
  throw createError({ statusCode: 404, statusMessage: 'Topic not found' });
}

const { canonicalLocaleCode, localePath } = useWorkspaceLocale();
const siteUrl = 'https://workspace369.com';
const canonicalUrl = computed(() =>
  localizedUrl(siteUrl, `/learn/topics/${page.value.slug}`, canonicalLocaleCode.value),
);

useHead(() => ({
  title: page.value.metaTitle,
  meta: [
    { name: 'description', content: page.value.description },
    { name: 'twitter:title', content: page.value.metaTitle },
    { name: 'twitter:description', content: page.value.description },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: page.value.metaTitle },
    { property: 'og:description', content: page.value.description },
    { property: 'og:url', content: canonicalUrl.value },
  ],
  link: [{ key: 'canonical', rel: 'canonical', href: canonicalUrl.value }],
  script: [
    {
      key: `topic-${page.value.slug}`,
      type: 'application/ld+json',
      children: JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'CollectionPage',
            '@id': `${canonicalUrl.value}#collection`,
            name: page.value.metaTitle,
            url: canonicalUrl.value,
            description: page.value.description,
            isPartOf: { '@id': `${siteUrl}/#website` },
          },
          {
            '@type': 'FAQPage',
            '@id': `${canonicalUrl.value}#faq`,
            mainEntity: page.value.faq.map((item) => ({
              '@type': 'Question',
              name: item.q,
              acceptedAnswer: { '@type': 'Answer', text: item.a },
            })),
          },
          {
            '@type': 'BreadcrumbList',
            '@id': `${canonicalUrl.value}#breadcrumbs`,
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Learning center',
                item: localizedUrl(siteUrl, '/learn', canonicalLocaleCode.value),
              },
              { '@type': 'ListItem', position: 3, name: page.value.title, item: canonicalUrl.value },
            ],
          },
        ],
      }),
    },
  ],
}));
</script>

<style scoped>
/* Same language as the approved learn hub: app neutral tokens, hairline 1px borders, 10px list surface. */
.topic-page {
  --ink: #0a0a0a;
  --ink2: #4f4f4f;
  --ink3: #717171;
  --edge: #e6e6e6;
  --hover: rgb(10 10 10 / 0.04);
  background: #ffffff;
  color: var(--ink);
}

.dark .topic-page {
  --ink: #fafafa;
  --ink2: #c2c2c2;
  --ink3: #9a9a9a;
  --edge: #2c2c2c;
  --hover: rgb(250 250 250 / 0.05);
  background: #121212;
}

.topic-hero {
  padding: 8.5rem 0 2.5rem;
}

.topic-breadcrumb {
  display: flex;
  flex-wrap: nowrap;
  min-width: 0;
  gap: 0.5rem;
  color: var(--ink3);
  font-size: 0.85rem;
}

.topic-breadcrumb > * {
  white-space: nowrap;
}

.topic-breadcrumb > span:last-child {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
}

.topic-breadcrumb a {
  color: var(--ink2);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.topic-hero h1 {
  margin-top: 1.25rem;
  max-width: 46rem;
  font-size: clamp(1.9rem, 5.2vw, 3.5rem);
  font-weight: 780;
  line-height: 1.05;
  letter-spacing: -0.01em;
  text-wrap: balance;
}

.topic-promise {
  max-width: 40rem;
  margin-top: 1.1rem;
  color: var(--ink2);
  font-size: clamp(0.95rem, 2.4vw, 1.05rem);
  line-height: 1.7;
}

.topic-answer {
  max-width: 40rem;
  margin-top: 0.9rem;
  color: var(--ink);
  font-size: clamp(0.95rem, 2.4vw, 1.05rem);
  line-height: 1.7;
}

.topic-section {
  border-top: 1px solid var(--edge);
  padding: 2.75rem 0;
}

.topic-split {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1.25rem;
}

.topic-section h2 {
  font-size: clamp(1.4rem, 2.6vw, 1.75rem);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: -0.005em;
}

.topic-table {
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 10px;
}

.topic-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 1rem;
  align-items: center;
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem;
  color: inherit;
  text-decoration: none;
  transition: background-color 120ms ease;
}

.topic-row:first-child {
  border-top: 0;
}

.topic-row:hover,
.topic-row:focus-visible {
  background: var(--hover);
}

.topic-row-main {
  display: grid;
  gap: 0.2rem;
  min-width: 0;
}

.topic-row-main strong {
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.35;
}

.topic-row-main > span {
  color: var(--ink2);
  font-size: 0.85rem;
  line-height: 1.5;
}

.topic-row i {
  color: var(--ink3);
  font-style: normal;
}

/* Guide steps, key terms and FAQ share the hairline list surface of the article rows. */
.topic-steps,
.topic-terms,
.topic-faq {
  overflow: hidden;
  border: 1px solid var(--edge);
  border-radius: 10px;
}

.topic-steps {
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: step;
}

.topic-steps li {
  display: grid;
  gap: 0.2rem;
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem 0.9rem 3rem;
  position: relative;
  counter-increment: step;
}

.topic-steps li:first-child,
.topic-terms > div:first-child,
.topic-faq details:first-child {
  border-top: 0;
}

.topic-steps li::before {
  content: counter(step);
  position: absolute;
  left: 1.1rem;
  color: var(--ink3);
  font-weight: 650;
  font-size: 0.9rem;
}

.topic-steps strong,
.topic-terms dt,
.topic-faq summary {
  font-size: 0.95rem;
  font-weight: 650;
  line-height: 1.35;
}

.topic-steps span,
.topic-terms dd,
.topic-faq p {
  margin: 0;
  color: var(--ink2);
  font-size: 0.85rem;
  line-height: 1.6;
}

.topic-terms > div {
  display: grid;
  gap: 0.2rem;
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem;
}

.topic-faq details {
  border-top: 1px solid var(--edge);
  padding: 0.9rem 1.1rem;
}

.topic-faq summary {
  cursor: pointer;
}

.topic-faq p {
  margin-top: 0.5rem;
}

@media (min-width: 1024px) {
  .topic-hero {
    padding-top: 9.5rem;
  }

  .topic-split {
    grid-template-columns: minmax(0, 22rem) minmax(0, 1fr);
    gap: 4rem;
  }

  .topic-section h2 {
    position: sticky;
    top: 8rem;
    align-self: start;
  }
}

@media (prefers-reduced-motion: reduce) {
  .topic-row {
    transition: none;
  }
}
</style>
